import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, LockKeyhole, ArrowUpRight } from "lucide-react";
import { NAV, SITE } from "../../content";
import { onAnchor } from "../../lib/scroll";

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    fn();
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const key = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", key);
    window.__lenis?.stop();
    return () => {
      window.removeEventListener("keydown", key);
      window.__lenis?.start();
    };
  }, [open]);

  return (
    <header
      data-testid="site-nav"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,padding,border-color] duration-500 ${
        scrolled ? "border-b border-white/10 bg-navy/85 py-3 backdrop-blur-xl" : "border-b border-transparent bg-transparent py-5"
      }`}
    >
      <nav aria-label="Hauptnavigation" className="mx-auto flex max-w-[1400px] items-center justify-between px-5 sm:px-8 lg:px-12">
        <a href="#start" onClick={onAnchor("start")} data-testid="nav-logo" aria-label="Schwimmschule Manta – zur Startseite">
          <img src="/images/manta-logo.png" alt="Schwimmschule Manta" width="436" height="136" className="h-9 w-auto drop-shadow-[0_2px_8px_rgba(6,18,42,0.6)] sm:h-10" />
        </a>

        <ul className="hidden items-center gap-1 xl:flex">
          {NAV.map((n) => (
            <li key={n.id}>
              <a
                href={`/#${n.id}`}
                onClick={onAnchor(n.id)}
                data-testid={`nav-${n.id}`}
                className="nav-link relative whitespace-nowrap px-2 py-2 text-[13px] font-semibold text-white/85 transition-colors hover:text-white 2xl:px-3 2xl:text-sm"
              >
                {n.label}
              </a>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-2 sm:gap-4">
          <a
            href={SITE.parentAppUrl}
            data-testid="nav-elternbereich"
            aria-label="App Login"
            className="inline-flex h-11 items-center gap-1.5 whitespace-nowrap rounded-full border border-white/40 px-4 text-xs font-bold uppercase tracking-[0.14em] text-white transition-colors hover:border-white hover:bg-white/10"
          >
            <LockKeyhole className="h-3.5 w-3.5" /> <span className="xl:hidden 2xl:inline">App Login</span><span className="hidden xl:inline 2xl:hidden">Login</span>
          </a>
          <a
            href="#kurse"
            onClick={onAnchor("kurse")}
            data-testid="nav-kurse-entdecken-btn"
            className="group hidden items-center gap-2 whitespace-nowrap rounded-full bg-white px-5 py-2.5 text-sm font-bold text-navy transition-colors hover:bg-sky-100 sm:inline-flex"
          >
            Kurse entdecken
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
          </a>
          <button
            type="button"
            onClick={() => setOpen(true)}
            aria-label="Menü öffnen"
            aria-expanded={open}
            aria-controls="mobile-menu"
            data-testid="nav-mobile-toggle"
            className="flex h-11 w-11 items-center justify-center rounded-full border border-white/30 text-white transition-colors hover:bg-white/10 xl:hidden"
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            id="mobile-menu"
            role="dialog"
            aria-modal="true"
            aria-label="Menü"
            data-testid="mobile-menu"
            initial={{ clipPath: "inset(0 0 100% 0)" }}
            animate={{ clipPath: "inset(0 0 0% 0)" }}
            exit={{ clipPath: "inset(0 0 100% 0)" }}
            transition={{ duration: 0.6, ease: [0.76, 0, 0.24, 1] }}
            className="fixed inset-0 z-[60] flex h-[100dvh] flex-col bg-navy px-6 pb-8 pt-5"
          >
            <div className="flex items-center justify-between">
              <img src="/images/manta-logo.png" alt="Schwimmschule Manta" className="h-9 w-auto" />
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Menü schließen"
                data-testid="mobile-menu-close"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-white/30 text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <ul className="mt-10 flex flex-1 flex-col">
              {NAV.map((n, i) => (
                <motion.li key={n.id} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 + i * 0.05 }}>
                  <a
                    href={`/#${n.id}`}
                    onClick={onAnchor(n.id, () => setOpen(false))}
                    data-testid={`mobile-nav-${n.id}`}
                    className="flex items-baseline gap-4 border-b border-white/10 py-3.5 text-lg font-normal tracking-wide text-white/90 transition-colors hover:text-white"
                  >
                    <span className="w-5 text-[11px] font-semibold text-sky-300/80">0{i + 1}</span>
                    {n.label}
                  </a>
                </motion.li>
              ))}
            </ul>
            <div className="flex flex-col gap-3">
              <a href="#kurse" onClick={onAnchor("kurse", () => setOpen(false))} data-testid="mobile-nav-kurse-btn" className="rounded-full bg-white py-4 text-center font-bold text-navy">
                Kurse entdecken
              </a>
              <a href={SITE.parentAppUrl} data-testid="mobile-nav-elternbereich" className="flex items-center justify-center gap-2 rounded-full border border-white/30 py-4 text-sm font-bold text-white">
                <LockKeyhole className="h-4 w-4" /> App Login
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
